import React, { PureComponent } from 'react';
import max from 'lodash/max';
import compact from 'lodash/compact';
import { Trans, t } from '@lingui/macro';
import Switch from 'rc-switch';
import Plotly from '../utils/plotly.bundle';
import { nFormatter, numberToSplitExpress } from '../utils/index';
import { YEARLY_REPORT } from '../utils/static';

const blue = '#497EE8';
const purple = 'rgb(148, 103, 189)';
const orange = '#F1AA66';
const green = '#579930';

class SystemDailyDeal extends PureComponent {
    constructor(props) {
        super(props);

        const { data } = props;
        let totalDay = 0;
        let totalTransaction = 0;
        // because eslint for-in guard...
        for (const year in data) {
            if (data[year]) {
                for (const month in data[year]) {
                    if (data[year][month]) {
                        for (const day in data[year][month]) {
                            totalDay += 1;
                            if (data[year][month][day]) totalTransaction += data[year][month][day];
                        }
                    }
                }
            }
        }
        this.state = {
            showMonthly: false,
            avgTransaction: numberToSplitExpress(
                Math.round(totalTransaction / totalDay),
            ),
        };
    }

    componentDidMount() {
        this.draw();
    }

    handleSwitchChange = (value) => {
    // trackBothEvent('System', 'Switch Daily/Monthly', value ? 'Show Monthly' : 'Show Daily');
        this.setState({ showMonthly: value }, () => this.draw());
    }

    draw = () => {
        const { showMonthly } = this.state;
        const drawData = showMonthly ? this.drawMonthly() : this.drawDaily();
        const { data } = drawData;
        let { layout } = drawData;
        if (this.props.isMobile) {
            layout = Object.assign({}, layout, {
                maring: {
                    l: 0,
                    r: 0,
                    t: 10,
                },
            });
        }
        Plotly.newPlot('system-daily-deal', data, layout, { displayModeBar: false });
    }

    drawDaily = () => {
        const { hasWeather } = this.props;
        const { sunnyData, rainData } = this._generateDailyData();

        const data = [sunnyData, rainData];

        const layout = {
            showlegend: hasWeather,
            legend: {
                x: 0,
                y: 100,
            },
            margin: {
                l: 20,
                r: 20,
            },
            xaxis: {
                type: 'date',
                tickformat: '%Y/%m',
                hoverformat: '%Y-%m-%d',
            },
            yaxis: {
                range: [0, max(data[0].y) + max(data[0].y) * 0.02],
                titlefont: { color: 'white' },
                tickfont: { color: 'white' },
            },
            barmode: 'relative',
        };
        return { data, layout };
    }

    drawMonthly = () => {
        const { hasWeather } = this.props;
        const {
            transactionData,
            rainData,
            snowData,
            temperatureData,
        } = this._generateMonthlyData();
        let data = [transactionData];
        if (hasWeather) data = data.concat([rainData, temperatureData]);
        if (snowData) data.push(snowData);
        const layout = {
            showlegend: true,
            legend: { orientation: 'h' },
            yaxis: {
                title: t({ id: 'common.transaction' }),
                range: [0, max(data[0].y) + max(data[0].y) * 0.02],
            },
            yaxis2: {
                title: t({ id: 'system.monthlyRainDay' }),
                titlefont: { color: 'white' },
                tickfont: { color: 'white' },
                overlaying: 'y',
                side: 'right',
                range: [0, 100],
            },
        };
        return { data, layout };
    }

    _generateMonthlyData = () => {
        const {
            data, weatherDaily, hasWeather,
        } = this.props;

        const transactionData = {
            x: [],
            y: [],
            text: [],
            hoverinfo: 'text',
            type: 'scatter',
            name: t({ id: 'common.transaction' }),
        };
        const temperatureData = {
            x: [],
            y: [],
            text: [],
            hoverinfo: 'text',
            type: 'scatter',
            yaxis: 'y2',
            name: t({ id: 'common.avgTemp' }),
            marker: {
                color: green,
                size: 5,
            },
            line: {
                color: green,
                width: 1,
            },
        };
        const rainData = {
            x: [],
            y: [],
            text: [],
            hoverinfo: 'text',
            type: 'scatter',
            yaxis: 'y2',
            name: t({ id: 'system.monthlyRainDay' }),
            marker: {
                color: purple,
                size: 5,
            },
            line: {
                color: purple,
                width: 1,
            },
        };
        let snowData = {
            x: [],
            y: [],
            text: [],
            hoverinfo: 'text',
            type: 'scatter',
            yaxis: 'y2',
            name: t({ id: 'system.monthlySnowDay' }),
            marker: {
                color: orange,
                size: 5,
            },
            line: {
                color: orange,
                width: 1,
            },
        };

        for (const year in data) {
            // eslint-disable-line guard-for-in
            for (const month in data[year]) {
                // eslint-disable-line guard-for-in
                let num = 0;
                let rainDay = 0;
                let snowDay = 0;
                let totalTemperature = 0;
                for (const day in data[year][month]) {
                    // eslint-disable-line guard-for-in
                    const completeDate = this._generateCompleteDate(year, month, day);
                    if (hasWeather && weatherDaily[completeDate]) {
                        if (weatherDaily[completeDate].rain > 0) rainDay += 1;
                        if (weatherDaily[completeDate].snow > 0) snowDay += 1;
                        totalTemperature += parseFloat(weatherDaily[completeDate].meantempm);
                    }
                    num += data[year][month][day];
                }

                const date = `${year}/${month}`;
                const avgTemp = Math.round(
                    totalTemperature / Object.keys(data[year][month]).length,
                );

                transactionData.y.push(num);
                transactionData.x.push(date);
                transactionData.text.push(`${t({ id: 'common.transaction' })}: ${nFormatter(num)}`);
                rainData.y.push(rainDay);
                rainData.x.push(date);
                rainData.text.push(`${t({ id: 'system.monthlyRainDay' })}: ${rainDay} ${t({ id: 'common.day' })}`);
                snowData.y.push(snowDay);
                snowData.x.push(date);
                snowData.text.push(`${t({ id: 'system.monthlySnowDay' })}: ${snowDay} ${t({ id: 'common.day' })}`);
                temperatureData.y.push(avgTemp);
                temperatureData.x.push(date);
                temperatureData.text.push(`${t({ id: 'common.avgTemp' })}: ${avgTemp} °C`);
            }
        }
        // if it didnt have snow day, hide it
        if (compact(snowData.y).length === 0) snowData = false;
        return {
            transactionData, rainData, snowData, temperatureData,
        };
    }

    _generateDailyData = () => {
        const {
            data, weatherDaily, hasWeather,
        } = this.props;
        const labels = [];
        const sunnyData = {
            x: [],
            y: [],
            text: [],
            hoverinfo: 'x+text',
            hoverlabel: {
                font: {
                    color: 'white',
                },
            },
            type: 'bar',
            marker: { color: blue },
            name: t({ id: 'common.sunny' }),
        };
        const rainData = {
            x: [],
            y: [],
            text: [],
            hoverinfo: 'x+text',
            type: 'bar',
            marker: { color: green },
            name: t({ id: 'common.rain' }),
        };

        for (const year in data) {
            // eslint-disable-line guard-for-in
            for (const month in data[year]) {
                // eslint-disable-line guard-for-in
                for (const day in data[year][month]) {
                    // eslint-disable-line guard-for-in
                    const num = data[year][month][day];
                    const date = `${year}-${month}-${day}`;
                    const completeDate = this._generateCompleteDate(year, month, day);
                    if (
                        hasWeather
            && weatherDaily[completeDate]
            && weatherDaily[completeDate].rain > 0
                    ) {
                        sunnyData.y.push(0);
                        rainData.y.push(num);
                        sunnyData.text.push('');
                        rainData.text.push(`${t({ id: 'common.transaction' })}: ${num}`);
                    } else {
                        sunnyData.y.push(num);
                        rainData.y.push(0);
                        sunnyData.text.push(`${t({ id: 'common.transaction' })}: ${num}`);
                        rainData.text.push('');
                    }
                    labels.push(date);
                }
            }
        }
        sunnyData.x = labels;
        rainData.x = labels;
        return { sunnyData, rainData };
    }

    _generateCompleteDate = (year, _month, _day) => {
        const month = _month.length === 1 ? `0${_month}` : _month;
        const day = _day.length === 1 ? `0${_day}` : _day;
        return year + month + day;
    }

    render() {
        const {
            reportType,
        } = this.props;
        const { avgTransaction } = this.state;
        // const note = [t({ id: 'system.note.item1' })];
        return (
            <div>
                {reportType === YEARLY_REPORT
                    ? (
                        <div className="switch-wrapper">
                            <span>
                                <Trans id="common.daily" />
                            </span>
                            <Switch onChange={this.handleSwitchChange} />
                            <span>
                                <Trans id="common.monthly" />
                            </span>
                        </div>
                    )
                    : null}
                <div id="system-daily-deal" />
                <p style={{ textAlign: 'left', fontWeight: 'bold' }}>
                    *
                    <Trans id="common.averageDailyUse" />
                    :
                    {avgTransaction}
                    {' '}
                    /
                    <Trans id="common.day" />
                </p>
                {/* {note.map(item => (
                    <p key={item} style={{ textAlign: 'left', fontWeight: 'bold' }}>
                        *
                        {item}
                    </p>
                ))} */}
            </div>
        );
    }
}

export default SystemDailyDeal;
